import dataUtils from '@/common/dataUtils';
import { push } from '@/common/utils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import { SYNC_STATES } from '@/store/sync/sync';
import gallerysApi from './gallerysApi';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import i18n from '@/plugins/i18n';

const state = {
  filteredGalleries: [],
  galleries: [],
  galleriesById: {},
  galleryUserStates: [],
  groups: [],
  groupsByInstance: {},
  images: [],
  imagesById: {},
  instance: null,
  selectedGalleryId: null,
  selectedGroupIndex: 0,
  selectedGroupId: 0,
  selectedGroupName: '',
  thumbnails: [],
  unwatchCount: {},
  userId: null,
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.galleries.filter((g) => g.instance == instance).filter((g) => !g._userState.dateread).length;
  },
};

const mutations = {
  setData(state, data) {
    Object.keys(data).forEach((k) => {
      state[k] = data[k];
    });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    //no-ops implementation
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm_Image({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.images,
      titleSearchAttr: null,
      contentSearchAttr: 'description',
      searchCriteriaArray: filterCriteria,
      options,
    });
  },
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.galleries,
      titleSearchAttr: 'name',
      contentSearchAttr: 'description',
      searchCriteriaArray: filterCriteria,
      options,
    });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // const { groups, galleries, images, galleryUserState } = state;
    // await updateModuleCache(moduleIndex.api, { groups, galleries, images, galleryUserState });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.images, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const result = await gallerysApi.getGalleries();
      let { groups, galleries, images, galleryUserState } = result.data;

      const galleriesById = dataUtils.buildIdMap(galleries, 'id');
      const galleryUserStateByGalleryId = dataUtils.buildIdMap(galleryUserState, 'galleryIdFk');

      galleries.forEach((g) => {
        let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: MODULE_ID });

        g._displayLocation = '';
        g._moduleTitle = moduleConfig ? moduleConfig.title : 'Galerie';
        g._moduleId = MODULE_ID;

        g._path = `/${moduleIndex.path}/cards/${g.instance}/${g.id}`; //TODO: Create route for opening fahrdienst document directly

        // Create a userState object in case it does not exist
        if (galleryUserStateByGalleryId[g.id]) {
          g._userState = galleryUserStateByGalleryId[g.id];
        } else {
          // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
          g._userState = { galleryIdFk: g.id, dateread: null };
        }
        g._userState._type = 'galleryUserState';
        g._images = [];
      });

      if (images) {
        images.forEach((i) => {
          const gallery = galleriesById[i.galleryIdFk];
          if (gallery) {
            if (i.type === 'teaser') {
              gallery._teaser = i;
            } else {
              push(gallery, '_images', i);
            }
          }
          let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: MODULE_ID });
          i._displayLocation = '';
          i._moduleTitle = moduleConfig ? moduleConfig.title : 'Galerie';
          i._path = `/${moduleIndex.path}/carousel/${gallery.instance}/${i.galleryIdFk}/${i.id}`; //TODO: Create route for opening fahrdienst document directly
        });
      }
      commit('setData', { groups, galleries, galleriesById, images, galleryUserState });
    } catch (err) {
      console.error('Failed to load gallery metadata', err);
      commit(
        'sync/setSyncState',
        {
          moduleId: MODULE_ID,
          syncState: SYNC_STATES.FAILURE,
          msg: `${err.code}:${err.message}`,
        },
        { root: true }
      );
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
  markAsRead({ commit, dispatch, state }, gallery) {
    console.debug('markAsRead', gallery);
    if (gallery) {
      let type = i18n.t(`type.${MODULE_ID}`); //e.g. type.video or type.news
      let stateMsg = i18n.t(`${'seen'}.${MODULE_ID}`);
      let actionName = i18n.t('action.marked');
      let sendboxTitle = gallery.name;
      let snackbarText = i18n.t('markAsMsg', { type, title: gallery.name, state: stateMsg, actionName });

      let action = 'markAsRead2';
      let galleryFlat = { ...gallery };
      delete galleryFlat._images;

      dispatch(
        `sendbox/${action}`,
        {
          item: galleryFlat,
          sendboxTitle,
          snackbarText,
          immediate: true,
        },
        { root: true }
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
